/**
 * Color parsing — CSS hex → packed ARGB uint.
 *
 * SciChart renderable series and palette providers take colors as ARGB uints
 * (0xAARRGGBB, the value `parseColorToUIntArgb` produces). We compute the same
 * value here, in pure JS, so palette resolution is testable without SciChart.
 */

export interface Rgba {
  /** 0..255 */ r: number;
  /** 0..255 */ g: number;
  /** 0..255 */ b: number;
  /** 0..255 */ a: number;
}

/**
 * Parse a CSS hex color. Accepts `#RGB`, `#RGBA`, `#RRGGBB`, `#RRGGBBAA`
 * (alpha last, as in CSS). The leading `#` is optional.
 */
export function parseHexColor(input: string): Rgba {
  let hex = input.trim();
  if (hex.startsWith("#")) hex = hex.slice(1);

  if (hex.length === 3 || hex.length === 4) {
    // Expand shorthand: #RGB(A) → #RRGGBB(AA)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if ((hex.length !== 6 && hex.length !== 8) || !/^[0-9a-fA-F]+$/.test(hex)) {
    throw new Error(`Invalid hex color: "${input}"`);
  }

  const num = Number.parseInt(hex, 16);
  const hasAlpha = hex.length === 8;
  const r = (num >>> (hasAlpha ? 24 : 16)) & 0xff;
  const g = (num >>> (hasAlpha ? 16 : 8)) & 0xff;
  const b = (num >>> (hasAlpha ? 8 : 0)) & 0xff;
  const a = hasAlpha ? num & 0xff : 0xff;
  return { r, g, b, a };
}

/** Pack RGBA channels into an ARGB uint (0xAARRGGBB). */
export function toArgb({ r, g, b, a }: Rgba): number {
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

/** Parse a CSS hex color directly to an ARGB uint. */
export function parseColorToArgb(input: string): number {
  return toArgb(parseHexColor(input));
}

/** Multiply a color's alpha by `opacity` (0..1), returning an ARGB uint. */
export function withOpacityArgb(input: string, opacity: number): number {
  const c = parseHexColor(input);
  const clamped = Math.max(0, Math.min(1, opacity));
  return toArgb({ ...c, a: Math.round(clamped * c.a) });
}